import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { motion, useAnimation, useInView } from "framer-motion";
import b1 from "../../assets/homeAssets/p1.webp";
import b2 from "../../assets/homeAssets/p2.webp";
import b3 from "../../assets/homeAssets/p3.webp";
import b4 from "../../assets/homeAssets/p4.webp";
import b5 from "../../assets/homeAssets/p5.png";
import b6 from "../../assets/homeAssets/p6.jpg";
import { Link } from "react-router-dom";

const Banner = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const animation = useAnimation();

  useEffect(() => {
    if (isInView) {
      animation.start("visible");
    }
    // eslint-disable-next-line
  }, [isInView]);

  return (
    <Container>
      {/* Dot Background */}
      <div className="dot-background" />
      <div className="dot-mask" />

      <div className="content">
        <motion.div
          className="text"
          initial="hidden"
          animate="show"
          variants={stagger}
        >
          <BoxReveal>
            <motion.h1 variants={boxRevealItem}>
              Telekomanda <br />
              <span>që po e kërkon</span> <br /> është këtu!
            </motion.h1>
          </BoxReveal>

          <BoxReveal>
            <motion.p variants={boxRevealItem}>
              Mbi 100 lloje të ndryshme të telekomandave për TV i gjeni këtu!
            </motion.p>
          </BoxReveal>

          <motion.div className="btns">
            <Link to={"/produktet"}>
              <motion.button
                variants={{
                  hidden: { opacity: 0, y: 100, scale: 0.4 },
                  visible: { opacity: 1, y: 0, scale: 1 },
                }}
                initial="hidden"
                animate={animation}
                transition={{ duration: 0.4, delay: 0.4, type: "spring" }}
              >
                Më Shumë
              </motion.button>
            </Link>
            <Link
              to={"/kontakti"}
              style={{ borderRadius: "15px", background: "none" }}
            >
              <motion.button
                variants={{
                  hidden: { opacity: 0, y: 100, scale: 0.4 },
                  visible: { opacity: 1, y: 0, scale: 1 },
                }}
                initial="hidden"
                animate={animation}
                transition={{ duration: 0.4, delay: 0.6, type: "spring" }}
              >
                Kontakti
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>

        <div className="images" ref={ref}>
          {[
            { src: b1, alt: "img1", rotate: "-10deg", delay: 0.3, z: 1 },
            { src: b2, alt: "img2", rotate: "-5deg", delay: 0.5, z: 2 },
            { src: b3, alt: "img3", rotate: "0deg", delay: 0.7, z: 3 },
            { src: b4, alt: "img4", rotate: "5deg", delay: 0.9, z: 4 },
            { src: b5, alt: "img5", rotate: "1deg", delay: 1.1, z: 5 },
            { src: b6, alt: "img6", rotate: "-10deg", delay: 1.3, z: 5 },
          ].map(({ src, alt, rotate, delay, z }) => (
            <div
              key={alt}
              className="card"
              style={{ transform: `rotate(${rotate})`, zIndex: z }}
            >
              <motion.img
                variants={{
                  hidden: { opacity: 0, y: 130, scale: 0.4 },
                  visible: { opacity: 1, y: 0, scale: 1 },
                }}
                initial="hidden"
                animate={animation}
                transition={{ duration: 1.4, delay, type: "spring" }}
                src={src}
                alt={alt}
              />
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
};

/* ── Box reveal wrapper ─────────────────────────────────────── */
const BoxReveal = ({ children }) => (
  <motion.div
    style={{ overflow: "hidden", display: "inline-block", width: "100%" }}
    variants={{
      hidden: {},
      show: { transition: { staggerChildren: 0.1 } },
    }}
  >
    {children}
  </motion.div>
);

/* ── Variants ───────────────────────────────────────────────── */
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.2 } },
};

const boxRevealItem = {
  hidden: { y: "100%", opacity: 0 },
  show: {
    y: "0%",
    opacity: 1,
    transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] },
  },
};

/* ── Styles ─────────────────────────────────────────────────── */
const Container = styled.div`
  position: relative;
  padding: 10em 7% 5%;
  /* min-height: 80vh; */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  /* ── Dot background ── */
  .dot-background {
    position: absolute;
    inset: 0;
    background-size: 20px 20px;
    background-image: radial-gradient(#d4d4d4 1px, transparent 1px);
    z-index: 0;
  }

  .dot-mask {
    position: absolute;
    inset: 0;
    background: white;
    mask-image: radial-gradient(ellipse at center, transparent 20%, black);
    -webkit-mask-image: radial-gradient(
      ellipse at center,
      transparent 20%,
      black
    );
    z-index: 1;
  }

  /* ── Content ── */
  .content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  /* ── Text block ── */
  .text {
    display: flex;
    flex-direction: column;
    align-items: center;

    h1 {
      text-align: center;
      font-family: "Poppins";
      font-weight: 300;
      line-height: 1em;
      font-size: 3.5vw;
      padding-bottom: 10px;
      span {
        color: var(--primaryColor);
      }
    }

    p {
      color: gray;
      margin-top: 0.5em;
      text-align: center;
      font-size: 1rem;
    }

    .btns {
      margin-top: 1.5em;
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      justify-content: center;

      button {
        font-family: "Comfortaa";
        background: #578bda;
        color: white;
        border-radius: 15px;
        padding: 12px 17px;
        border: none;
        font-size: 17px;
        cursor: pointer;
      }

      :nth-child(2) button {
        background: #578bda88;
      }
    }
  }

  /* ── Images row — desktop: single row ── */
  .images {
    display: flex;
    justify-content: center;
    gap: 0.1em;
    margin-top: 3em;
    flex-wrap: wrap;

    .card {
      transition: all 0.3s ease;
      transform-origin: center center;

      img {
        width: 100px;
        height: 140px;
        background-color: white;
        object-fit: cover;
        border-radius: 15px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
      }
    }
  }

  /* ── Tablet (max 1024px) ── */
  @media (max-width: 1024px) {
    padding: 10em 5% 5%;
    min-height: auto;

    .text {
      h1 {
        font-size: 4.2em;
      }
      p {
        font-size: 0.95rem;
      }
    }

    .images .card img {
      width: 145px;
      height: 178px;
    }
  }

  /* ── Large mobile / small tablet (max 768px) ── */
  @media (max-width: 768px) {
    padding: 7em 24px 40px;
    padding-bottom: 8em;

    .text {
      h1 {
        font-size: 3em;
        line-height: 1.1em;
      }
      p {
        font-size: 0.9rem;
        padding: 0 8px;
      }
      .btns button {
        font-size: 15px;
        padding: 10px 20px;
      }
    }

    /* 2 rows of 3, with vertical overlap between rows */
    .images {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      margin-top: 2em;
      width: 100%;

      /* pull the bottom row up so it overlaps the top row */
      .card:nth-child(4),
      .card:nth-child(5),
      .card:nth-child(6) {
        margin-top: -40px;
      }

      /* flatten rotations for the grid layout */
      .card:nth-child(1) {
        transform: rotate(-5deg) !important;
      }
      .card:nth-child(2) {
        transform: rotate(-2deg) !important;
      }
      .card:nth-child(3) {
        transform: rotate(0deg) !important;
      }
      .card:nth-child(4) {
        transform: rotate(2deg) !important;
      }
      .card:nth-child(5) {
        transform: rotate(-1deg) !important;
      }
      .card:nth-child(6) {
        transform: rotate(5deg) !important;
      }

      .card img {
        width: 100%;
        height: auto;
      }
    }
  }

  /* ── Mobile (max 480px) ── */
  @media (max-width: 480px) {
    padding: 8em 16px 32px;

    .text {
      h1 {
        font-size: 10.2vw;
        line-height: 1.15em;
      }
      p {
        font-size: 0.85rem;
      }
      .btns {
        gap: 8px;
        button {
          font-size: 14px;
          padding: 10px 18px;
        }
      }
    }

    .images {
      gap: 6px;

      .card:nth-child(4),
      .card:nth-child(5),
      .card:nth-child(6) {
        margin-top: -32px;
      }

      .card img {
        border-radius: 10px;
      }
    }
  }

  /* ── Extra small (max 360px) ── */
  @media (max-width: 360px) {
    .text h1 {
      font-size: 1.9em;
    }

    .images {
      gap: 4px;

      .card img {
        height: 88px;
      }

      .card:nth-child(4),
      .card:nth-child(5),
      .card:nth-child(6) {
        margin-top: -28px;
      }
    }
  }
`;

export default Banner;
